import { map } from 'lodash';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  ACTIVE_PAYOUT_ORDERS_STATUSES,
  MANUAL_PAYOUT_STATUS_DETAILS,
} from 'constants/order.constants';
import { AdditionalOrderColumn, FilterDefinitionType, OrdersTab } from 'enums';
import { useMeta, useOrderQueryFilters } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { FilterDefinition, OrderFilters } from 'types';
import { orderUtils } from 'utils';

import { ManageOrdersList } from '../ManageOrderList';

export const ActiveOrders: React.FC = () => {
  useOrderQueryFilters();

  const { isPayoutManualDistributionEnabled } = useMeta();

  const { t: tFilters } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders.filters',
  });

  /**
   * Manual-queue orders sit in `new`, so the status filter alone cannot tell them apart from
   * orders that are merely passing through it — the sub-state is what an operator filters by.
   */
  const manualStatusDetailsFilter: FilterDefinition<OrderFilters> = useMemo(
    () => ({
      label: tFilters('status_details'),
      name: 'statusDetails',
      type: FilterDefinitionType.Select,
      options: map(MANUAL_PAYOUT_STATUS_DETAILS, (statusDetails) => ({
        value: statusDetails,
        label: orderUtils.getPayoutStatusDetailsLabel(statusDetails),
      })),
      getDisplayName: orderUtils.getPayoutStatusDetailsLabel,
    }),
    [tFilters],
  );

  const additionalFilters: FilterDefinition<OrderFilters>[] = useMemo(
    () => [
      orderUtils.getStatusFilterDefinition(ACTIVE_PAYOUT_ORDERS_STATUSES),
      ...(isPayoutManualDistributionEnabled ? [manualStatusDetailsFilter] : []),
    ],
    [isPayoutManualDistributionEnabled, manualStatusDetailsFilter],
  );

  return (
    <ManageOrdersList
      tab={OrdersTab.Active}
      additionalFilters={additionalFilters}
      additionalColumns={[
        AdditionalOrderColumn.StatusActive,
        AdditionalOrderColumn.OrderProcessedBy,
        AdditionalOrderColumn.TraderPayment,
      ]}
    />
  );
};
